import { SHOPIFY_STORE_URL } from '../config';

/**
 * Shopify /cart/add.js returns different shapes (message, description, errors object).
 */
function parseCartAddErrorPayload(errorData) {
  if (!errorData || typeof errorData !== 'object') {
    return 'Unknown error';
  }
  if (typeof errorData.message === 'string' && errorData.message.trim()) {
    return errorData.message.trim();
  }
  if (typeof errorData.description === 'string' && errorData.description.trim()) {
    return errorData.description.trim();
  }
  if (typeof errorData.error === 'string' && errorData.error.trim()) {
    return errorData.error.trim();
  }
  const { errors } = errorData;
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors)) return errors.filter(Boolean).join(' ');
  if (errors && typeof errors === 'object') {
    const parts = Object.values(errors).flat();
    if (parts.length) return parts.filter(Boolean).join(' ');
  }
  return 'Unknown error';
}

/**
 * Explains common false "sold out" for made-to-order variants; clarifies catalog USD in Shopify's message.
 */
function enrichCartAddErrorMessage(message) {
  const m = message || '';
  if (!/sold out|not available|inventory|unavailable/i.test(m)) {
    return m;
  }
  return (
    `${m}\n\n` +
    `If this is a custom blend: Shopify may block add-to-cart when inventory is tracked at 0 or locations are missing. ` +
    `The price in Shopify’s message is the variant’s catalog option (often USD), not your storefront total. ` +
    `Store admin: run the custom-blend Shopify sync (untracked inventory) or in Admin → product variant set inventory to not tracked / continue selling when out of stock.`
  );
}

/** Max chars for line-item property value (stay under Shopify Ajax cart limits). */
const BLEND_DISPLAY_SNAPSHOT_MAX_CHARS = 7200;

/**
 * Snapshot of /api/blends/evaluate fields the customer saw in the builder (stored in DB via webhook).
 * Schema v=1 must match api.webhook_routes.DISPLAY_SNAPSHOT_SCHEMA_VERSION.
 *
 * For user_ratios blends the customer-supplied percentages are also captured
 * here so the order record can re-render exactly what was built.
 */
export function buildBlendDisplaySnapshot(blendResult, packetSize, preparationMethod) {
  if (!blendResult || !blendResult.success) {
    throw new Error('buildBlendDisplaySnapshot requires a successful blend result');
  }
  const matched = Array.isArray(blendResult.matched_recipes)
    ? blendResult.matched_recipes.slice(0, 3)
    : [];
  return {
    v: 1,
    captured_at: new Date().toISOString(),
    packet_size_grams: packetSize,
    preparation_method: preparationMethod,
    caffeine_mg: blendResult.caffeine_mg,
    caffeine_level: blendResult.caffeine_level,
    warnings: Array.isArray(blendResult.warnings) ? blendResult.warnings : [],
    price: blendResult.price,
    currency_code: blendResult.currency_code ?? null,
    currency_symbol: blendResult.currency_symbol ?? null,
    brewing_instructions: blendResult.brewing_instructions || {},
    matched_recipes: matched,
    mode: blendResult.mode || 'auto',
    custom_percentages: blendResult.custom_percentages ?? null,
  };
}

function serializeBlendDisplaySnapshot(snapshot) {
  let s = { ...snapshot };
  let json = JSON.stringify(s);
  if (json.length > BLEND_DISPLAY_SNAPSHOT_MAX_CHARS) {
    s = {
      ...s,
      matched_recipes: [],
      brewing_instructions: {},
    };
    json = JSON.stringify(s);
  }
  if (json.length > BLEND_DISPLAY_SNAPSHOT_MAX_CHARS) {
    throw new Error(
      'Blend display snapshot is too large for the cart. Remove recommendations or try fewer ingredients.',
    );
  }
  return json;
}

// Shopify integration for custom blends
export const shopifyCart = {
  // Add custom blend to cart using Shopify Ajax API
  // This adds to existing cart instead of creating new checkout
  addCustomBlend: async (blendData) => {
    const {
      selectedIngredients,
      ingredients,
      variant_id,
      product_id,
      packet_size,
      preparation_method,
      display_snapshot: displaySnapshotInput,
      mode = 'auto',
      custom_percentages = null,
      blend_name = null,
    } = blendData;

    // Do not add a line-item "Price" property: it duplicates the Shopify line price and
    // often shows a second number (USD vs Markets CAD). Order webhooks use Shopify line item price, not properties.
    console.log('[Shopify] Adding custom blend to cart:', {
      variant_id,
      product_id,
      packet_size,
    });

    try {
      // Format ingredients for display
      const ingredientNames = selectedIngredients
        .map(ingId => {
          const ing = ingredients.find(i => i.id === ingId);
          return ing ? ing.name : ingId;
        })
        .join(', ');

      const properties = {
        'Ingredients': ingredientNames,
        'Size': `${packet_size}g`,
        'Preparation': preparation_method,
        '_ingredient_ids': JSON.stringify(selectedIngredients),
        '_ingredients_csv': selectedIngredients.join(','),
        '_packet_size': packet_size.toString(),
        '_preparation_method': preparation_method,
        '_mode': mode,
      };
      // For user_ratios blends, ship the customer's percentages so the webhook
      // persists the exact composition without re-running the engine.
      if (mode === 'user_ratios' && custom_percentages) {
        properties._custom_percentages = JSON.stringify(custom_percentages);
      }
      if (displaySnapshotInput != null) {
        properties._blend_display_snapshot = serializeBlendDisplaySnapshot(displaySnapshotInput);
      }
      // Optional user-supplied personal name (already sanitized upstream).
      // We send it twice: visible `Blend Name` shows in the customer's Shopify
      // order detail page; `_blend_name` is what the webhook reads to persist
      // to the database and render on the label.
      if (blend_name) {
        properties['Blend Name'] = blend_name;
        properties._blend_name = blend_name;
      }

      // Use Shopify Ajax Cart API to add to existing cart
      const response = await fetch(`${SHOPIFY_STORE_URL}/cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variant_id,
            quantity: 1,
            properties,
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Shopify] Add to cart failed:', response.status, errorData);
        const raw = parseCartAddErrorPayload(errorData);
        const detail = enrichCartAddErrorMessage(raw);
        throw new Error(detail || `Failed to add to cart: ${response.status}`);
      }

      const result = await response.json();
      console.log('[Shopify] Added to cart successfully:', result);

      // Redirect to cart page to show the updated cart
      window.location.href = `${SHOPIFY_STORE_URL}/cart`;

      return { success: true };

    } catch (error) {
      console.error('[Shopify] Error adding to cart:', error);
      throw error;
    }
  },

  goToCart: () => {
    window.top.location.href = `${SHOPIFY_STORE_URL}/cart`;
  },

  goToCheckout: () => {
    window.top.location.href = `${SHOPIFY_STORE_URL}/checkout`;
  },
};

export default shopifyCart;
